/**
 * Stundenplan (Roadmap F-22, Stufe 2).
 *
 * Eine Schulstunde ist ein woechentlicher Serientermin im Kalender - keine
 * eigene Datenhaltung (Leitlinie 5). Stunden nur in A- oder nur in
 * B-Wochen laufen "alle 2 Wochen" und starten in einer passenden Woche.
 * Hier steht nur die reine Umrechnung zwischen Termin und Stunde.
 */
import { abWocheVon } from '@/domain/school/weeks';
import { addDays, datePart, startOfWeek, weekday } from '@/lib/datetime';
import type { CalendarEvent, ID, LocalDate } from '@/domain/types';

/** In welchen Wochen die Stunde stattfindet. */
export type StundenWoche = 'jede' | 'A' | 'B';

/** Montag bis Freitag, wie `weekday()`: 1 = Montag. */
export const SCHULTAGE = [1, 2, 3, 4, 5] as const;
export const TAG_NAMEN: Record<number, string> = { 1: 'Mo', 2: 'Di', 3: 'Mi', 4: 'Do', 5: 'Fr' };

export interface Stunde {
  eventId: ID;
  subjectId: ID;
  tag: number;
  /** 'HH:mm' */
  von: string;
  bis: string;
  raum?: string;
  woche: StundenWoche;
}

export function stundenRegel(woche: StundenWoche): string {
  return woche === 'jede' ? 'FREQ=WEEKLY' : 'FREQ=WEEKLY;INTERVAL=2';
}

/**
 * Erster Termin der Serie: der Tag in dieser Woche - bei A/B die naechste
 * passende Woche. Liegt er schon zurueck, ist das kein Fehler: der
 * Stundenplan dieser Woche ist dann vollstaendig.
 */
export function ersterTag(tag: number, woche: StundenWoche, heute: LocalDate, aWocheAb: LocalDate): LocalDate {
  const kandidat = addDays(startOfWeek(heute), tag - 1);
  if (woche === 'jede' || abWocheVon(kandidat, aWocheAb) === woche) return kandidat;
  return addDays(kandidat, 7);
}

/** Termin -> Stunde. `null` fuer alles, was keine Schulstunde ist. */
export function stundeAusTermin(event: CalendarEvent, aWocheAb: LocalDate): Stunde | null {
  if (!event.subjectId || event.allDay || event.recurringEventId || !event.rrule?.startsWith('FREQ=WEEKLY')) return null;
  const start = String(event.start);
  const zweiwoechig = /INTERVAL=2/.test(event.rrule);
  return {
    eventId: event.id,
    subjectId: event.subjectId,
    tag: weekday(start),
    von: start.slice(11, 16),
    bis: String(event.end).slice(11, 16),
    raum: event.location,
    woche: zweiwoechig ? abWocheVon(datePart(start), aWocheAb) : 'jede',
  };
}

/** Stunden einer Woche (`null` = ohne A/B), je Tag nach Beginn sortiert. */
export function stundenDerWoche(stunden: Stunde[], woche: 'A' | 'B' | null): Map<number, Stunde[]> {
  const tage = new Map<number, Stunde[]>(SCHULTAGE.map((t) => [t, []]));
  for (const s of stunden) {
    if (woche && s.woche !== 'jede' && s.woche !== woche) continue;
    tage.get(s.tag)?.push(s);
  }
  for (const liste of tage.values()) liste.sort((a, b) => a.von.localeCompare(b.von));
  return tage;
}
